import {useHydration} from 'quickstart/hooks'
import {ComponentProps, ReactNode} from 'react'
import {DialogDisclosure, useDialogState} from 'reakit/Dialog'
import {partitionProps, STORYBOOK} from 'tizra'
import * as S from './styles'
import classnames from 'classnames'

export const useModalState = (
  options: Parameters<typeof useDialogState>[0] = undefined,
) => useDialogState({animated: true, ...options})

export type ModalState = ReturnType<typeof useModalState>

const MODAL_STATE_PROPS: (keyof ModalState)[] = [
  'baseId',
  'visible',
  'animated',
  'animating',
  'stopAnimation',
  'modal',
  'hide',
]

export type ModalProps = ModalState &
  ComponentProps<typeof S.Dialog> & {
    children: ReactNode
    hideOnClickOutside?: boolean | undefined
    onClose?: () => void | undefined
  }

const _Modal = (props: ModalProps) => {
  const [modal, {children, onClose, size = 'lg', ...rest}] = partitionProps(
    MODAL_STATE_PROPS,
    props,
  )

  const hide = () => {
    modal.hide()
    onClose?.()
  }

  const backdropClassName = STORYBOOK ? 'dialog-backdrop' : undefined

  const dialogClassName = STORYBOOK
    ? classnames(rest.className, 'dialog-dialog')
    : rest.className

  const rendered = (
    <S.Backdrop
      {...modal}
      className={backdropClassName}
      hideOnClickOutside={props.hideOnClickOutside ?? modal.modal}
    >
      <S.Dialog
        {...modal}
        {...rest}
        className={dialogClassName}
        hide={hide}
        size={size}
      >
        <S.Close onClick={hide} />
        {children}
      </S.Dialog>
    </S.Backdrop>
  )

  // https://github.com/facebook/react/issues/12615
  return useHydration(null, rendered)
}

export const Modal = Object.assign(_Modal, {
  Trigger: DialogDisclosure,
  Content: S.Content,
  Title: S.Title,
  Footer: S.Footer,
  Cover: S.Cover,
})
